// Authentication service — wraps the login API and token management

import mixpanel from 'mixpanel-browser';
import { apiRequest, type ApiError } from './api';

const TOKEN_KEY = 'auth_token';
const ORG_ID_KEY = 'organization_id';
const SCHOOL_ID_KEY = 'school_id';
const SCHOOL_NAME_KEY = 'school_name';
const ACADEMIC_SESSION_ID_KEY = 'academic_session_id';
const USER_NAME_KEY = 'user_name';
const PERMISSIONS_KEY = 'permissions';

interface LoginResponse {
    token: string;
    permissions?: string[];
    name?: string;
    role?: string;
}

export interface LoginData {
    token: string;
    permissions: string[];
    name: string | null;
    role: string | null;
}

/**
 * Authenticate with email & password.
 * Returns the full login payload including token and permissions.
 */
export async function login(email: string, password: string): Promise<LoginData> {
    const result = await apiRequest<LoginResponse>('/api/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
    });
    return {
        token: result.data.token,
        permissions: result.data.permissions ?? [],
        name: result.data.name ?? null,
        role: result.data.role ?? null,
    };
}

// ── Token helpers ──────────────────────────────────────

export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ORG_ID_KEY);
    localStorage.removeItem(SCHOOL_ID_KEY);
    localStorage.removeItem(SCHOOL_NAME_KEY);
    localStorage.removeItem(USER_NAME_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
}

// ── Permissions ──────────────────────────────────────

export function getPermissions(): string[] {
    try {
        return JSON.parse(localStorage.getItem(PERMISSIONS_KEY) ?? '[]');
    } catch {
        return [];
    }
}

export function setPermissions(permissions: string[]): void {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
}

export function hasPermission(perm: string): boolean {
    return getPermissions().includes(perm);
}

export function hasAnyPermission(perms: string[]): boolean {
    const stored = getPermissions();
    return perms.some(p => stored.includes(p));
}

// ── Current user ──────────────────────────────────────

export function getUserName(): string | null {
    return localStorage.getItem(USER_NAME_KEY);
}

export function setUserName(name: string): void {
    localStorage.setItem(USER_NAME_KEY, name);
}

export async function fetchAndStoreCurrentUser(): Promise<void> {
    try {
        const res = await apiRequest<{ name?: string; email?: string }>('/api/user');
        const name = res.data.name ?? res.data.email ?? null;
        const email = res.data.email ?? null;
        if (name) localStorage.setItem(USER_NAME_KEY, name);
        if (email) {
            mixpanel.identify(email);
            mixpanel.people.set({
                $name: name,
                $email: email,
            });
        }
    } catch {
        // Silently fail — initials will fall back to a placeholder
    }
}

export function isAuthenticated(): boolean {
    return !!getToken();
}

// ── Organization / School context ──────────────────────────

export function getOrganizationId(): string | null {
    return localStorage.getItem(ORG_ID_KEY);
}

export function getSchoolId(): string | null {
    return localStorage.getItem(SCHOOL_ID_KEY);
}

export function setSchoolId(id: string): void {
    localStorage.setItem(SCHOOL_ID_KEY, id);
}

export function clearSchoolId(): void {
    localStorage.removeItem(SCHOOL_ID_KEY);
}

/** Name of the currently selected school (kept in sync with the school id). */
export function getSchoolName(): string | null {
    return localStorage.getItem(SCHOOL_NAME_KEY);
}

export function setSchoolName(name: string): void {
    localStorage.setItem(SCHOOL_NAME_KEY, name);
}

export function clearSchoolName(): void {
    localStorage.removeItem(SCHOOL_NAME_KEY);
}

// ── Academic Session context ──────────────────────────

export function getAcademicSessionId(): string | null {
    return localStorage.getItem(ACADEMIC_SESSION_ID_KEY);
}

export function setAcademicSessionId(id: string): void {
    localStorage.setItem(ACADEMIC_SESSION_ID_KEY, id);
}

/**
 * Fetches the user's organization and school IDs and stores them in localStorage.
 * Should be called once after a successful login and on app load when a token exists.
 */
export async function fetchAndStoreUserContext(): Promise<void> {
    try {
        const [orgsRes, schoolsRes] = await Promise.all([
            apiRequest<{ id: string; name: string }[]>('/api/organizations'),
            apiRequest<{ id: string; name: string }[]>('/api/schools'),
        ]);
        const orgId = orgsRes.data[0]?.id;
        const defaultSchool = schoolsRes.data[0];
        // Only set defaults if nothing is stored — preserve user's manual selection
        if (orgId && !localStorage.getItem(ORG_ID_KEY)) localStorage.setItem(ORG_ID_KEY, orgId);
        if (defaultSchool && !localStorage.getItem(SCHOOL_ID_KEY)) {
            localStorage.setItem(SCHOOL_ID_KEY, defaultSchool.id);
            localStorage.setItem(SCHOOL_NAME_KEY, defaultSchool.name);
        }
    } catch {
        // Silently fail — context may load on next attempt
    }
}

// Re-export ApiError so consumers don't need to import from api.ts
export type { ApiError };
